import { useNavigate } from "react-router-dom";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";

/**
 * AttentionBadge — rote Ziffer für Team-Sidebar-Chips (Feb 2026).
 * Zeigt die Anzahl der roten Zustände (Handlungsbedarf) neben einem
 * Sidebar-Nav-Eintrag. Rendert NICHT wenn count <= 0.
 *
 * Prop `to` (Feb 2026 · Sidebar-Chip-Filter):
 *   Wenn gesetzt, wird der Badge klickbar UND navigiert per Router zum
 *   gefilterten Ziel (z. B. `/team/properties?filter=attention`). Klicks
 *   werden gestoppt (stopPropagation + preventDefault), damit sie NICHT die
 *   umgebende NavLink des Sidebar-Eintrags feuern. Ohne `to` verhält sich
 *   der Badge weiterhin passiv (rein visuell).
 */
export function AttentionBadge({ count, testid, small = false, inline = false, to, variant = "red" }) {
  const nav = useNavigate();
  const n = Number(count) || 0;
  if (n <= 0) return null;
  const label = n > 99 ? "99+" : String(n);
  // Feb 2026 · 10 Feb 2026 — Variante „blue" für den zweiten Projekt-Badge
  // (In Bearbeitung). Rot bleibt Default und die vorhandene Semantik.
  const badgeBg = variant === "blue" ? STATUS_COLORS.blue : variant === "gold" ? "#C9A961" : STATUS_COLORS.red;
  const fg = variant === "gold" ? "#3A2E12" : BRAND_COLORS.white;
  const noun = variant === "blue"
    ? `Projekt${n === 1 ? "" : "e"} in Bearbeitung`
    : variant === "gold"
      ? `neue Zuweisung${n === 1 ? "" : "en"} — noch nicht geöffnet`
      : `Punkt${n === 1 ? "" : "e"} mit Handlungsbedarf`;
  const aria = to ? `${n} ${noun} — klicken zum Filtern.` : `${n} ${noun}`;

  const commonProps = {
    "data-testid": testid,
    "aria-label": aria,
    ...(to ? {
      role: "button",
      tabIndex: 0,
      onClick: (e) => { e.stopPropagation(); e.preventDefault(); nav(to); },
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.stopPropagation();
          e.preventDefault();
          nav(to);
        }
      },
      title: variant === "blue"
        ? `Nur „In Bearbeitung" anzeigen (${n})`
        : variant === "gold"
          ? `${n} noch nicht geöffnet`
          : `Nur mit Handlungsbedarf anzeigen (${n})`,
    } : {}),
  };

  if (inline) {
    return (
      <span
        {...commonProps}
        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-bold rounded-full leading-none shadow-sm ml-1.5 ${to ? "cursor-pointer hover:scale-110 transition-transform" : ""}`}
        style={{ background: badgeBg, color: fg }}
      >
        {label}
      </span>
    );
  }
  const size = small
    ? "min-w-[16px] h-[16px] text-[9.5px] -top-1 -right-1"
    : "min-w-[18px] h-[18px] text-[10.5px] -top-1.5 -right-1.5";
  return (
    <span
      {...commonProps}
      className={`absolute ${size} px-1 rounded-full font-bold flex items-center justify-center leading-none ${to ? "cursor-pointer hover:scale-110 transition-transform pointer-events-auto" : "pointer-events-none"}`}
      style={{
        background: badgeBg,
        color: fg,
        boxShadow: "0 1px 3px rgba(0,0,0,0.25), 0 0 0 1.5px #FFFFFF",
      }}
    >
      {label}
    </span>
  );
}

export default AttentionBadge;
